import { useState } from 'react';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import type { Task } from '../App';

interface UserFormProps {
  taskName: string;
  customTask?: Task;
  submittedBy?: string;
  onComplete: (responses?: Record<string, string | number | boolean>) => void;
  onCancel: () => void;
}

export function UserForm({ taskName, customTask, submittedBy, onComplete, onCancel }: UserFormProps) {
  const [formData, setFormData] = useState<Record<string, string | string[] | boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      const prev = (formData[name] as string[]) || [];
      setFormData(fd => ({
        ...fd,
        [name]: checked ? [...prev, value] : prev.filter(v => v !== value),
      }));
    } else {
      setFormData(fd => ({ ...fd, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (customTask) {
      const visible = customTask.questions.filter(q => {
        if (!q.conditionalOn) return true;
        return formData[q.conditionalOn.questionId] === q.conditionalOn.value;
      });
      visible.forEach(q => {
        if (q.required !== false) {
          const v = formData[q.id];
          if (!v || (Array.isArray(v) && v.length === 0) || v === '') {
            newErrors[q.id] = 'This field is required';
          }
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Build responses: coerce numbers and yes/no
    const responses: Record<string, string | number | boolean> = {};
    if (customTask) {
      customTask.questions.forEach(q => {
        const val = formData[q.id];
        if (val === undefined || val === '') return;
        if (q.type === 'number') {
          responses[q.id] = parseFloat(val as string) || 0;
        } else if (q.type === 'yes-no') {
          responses[q.id] = (val as string) === 'Yes';
        } else if (Array.isArray(val)) {
          responses[q.id] = (val as string[]).join(', ');
        } else {
          responses[q.id] = val as string;
        }
      });
    }

    setSubmitted(true);
    setTimeout(() => onComplete(responses), 1800);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
        <div className="flex justify-center mb-4">
          <CheckCircle2 className="w-20 h-20 text-green-500" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Submitted!</h2>
        <p className="text-gray-600 text-lg">
          Daily report for <span className="font-semibold">{customTask?.title || taskName}</span> has been saved.
        </p>
        {submittedBy && <p className="text-gray-500 mt-1">Submitted by {submittedBy}</p>}
      </div>
    );
  }

  if (customTask) {
    const visibleQuestions = customTask.questions.filter(q => {
      if (!q.conditionalOn) return true;
      return formData[q.conditionalOn.questionId] === q.conditionalOn.value;
    });

    return (
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <button type="button" onClick={onCancel} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Tasks</span>
        </button>

        {visibleQuestions.map((question) => {
          const isRequired = question.required !== false;
          return (
            <div key={question.id}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {question.question}
                {isRequired && <span className="text-red-500"> *</span>}
                {!isRequired && <span className="text-gray-500 text-xs ml-1">(Optional)</span>}
              </label>

              {question.type === 'text' && (
                <input type="text" name={question.id} value={(formData[question.id] as string) || ''} onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors[question.id] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition`}
                  placeholder="Enter your answer..." />
              )}

              {question.type === 'number' && (
                <input type="number" name={question.id} value={(formData[question.id] as string) || ''} onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors[question.id] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition`}
                  placeholder="Enter a number..." />
              )}

              {question.type === 'yes-no' && (
                <div className="flex gap-4">
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} className={`flex items-center gap-3 px-6 py-3 border-2 rounded-lg cursor-pointer transition ${formData[question.id] === opt ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 hover:border-indigo-300'}`}>
                      <input type="radio" name={question.id} value={opt} checked={formData[question.id] === opt} onChange={handleChange} className="sr-only" />
                      <span className="font-medium">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'multiple-choice' && question.options && (
                <div className="space-y-2">
                  {question.options.map(opt => (
                    <label key={opt} className="flex items-center cursor-pointer">
                      <input type="radio" name={question.id} value={opt} checked={formData[question.id] === opt} onChange={handleChange} className="w-4 h-4 text-indigo-600" />
                      <span className="ml-2 text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'dropdown' && question.options && (
                <select name={question.id} value={(formData[question.id] as string) || ''} onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors[question.id] ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white`}>
                  <option value="">Select an option</option>
                  {question.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              )}

              {question.type === 'checkbox' && question.options && (
                <div className="space-y-2">
                  {question.options.map(opt => (
                    <label key={opt} className="flex items-center cursor-pointer">
                      <input type="checkbox" name={question.id} value={opt}
                        checked={((formData[question.id] as string[]) || []).includes(opt)}
                        onChange={handleChange} className="w-4 h-4 text-indigo-600 rounded" />
                      <span className="ml-2 text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {errors[question.id] && <p className="text-red-500 text-sm mt-1">{errors[question.id]}</p>}
            </div>
          );
        })}

        <div className="pt-4">
          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-6 rounded-lg transition shadow-lg">
            Submit Daily Report
          </button>
        </div>
      </form>
    );
  }

  // Fallback for tasks without questions
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
      <button type="button" onClick={onCancel} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition">
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Tasks</span>
      </button>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
        <textarea name="notes" value={(formData['notes'] as string) || ''} onChange={handleChange} rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
          placeholder="Describe today's activities..." />
      </div>
      <div className="pt-4">
        <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-6 rounded-lg transition shadow-lg">
          Submit Daily Report
        </button>
      </div>
    </form>
  );
}